import { Button } from 'antd';
import { useFormikContext } from 'formik';
import { type ChangeEvent, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import './CustomerLeadStepSalesTaxInfo.scss';

import AddPhotoIcon from 'src/assets/add-photo.svg?react';
import SalesTaxFileIcon from 'src/assets/sds-file-icon.svg?react';
import { ValidationError } from 'src/components/base';
import type { ICustomerLeadSalesTaxInfoTabFormValues } from 'src/components/customers/CustomerLeadTypes';
import { blobToBase64 } from 'src/helpers';
import { removeBase64Prefix } from 'src/helpers/removeBase64Prefix';
import { CustomersContext } from 'src/pages/CustomersPage/CustomersPage.context';
import type { IFilePayload } from 'src/types';

export function CustomerLeadStepSalesTaxFilesList() {
  const fileRef = useRef<HTMLInputElement>(null);

  const { values, errors, setFieldValue, setFieldTouched } = useFormikContext<ICustomerLeadSalesTaxInfoTabFormValues>();

  const isSalesTaxFilesDirty = useMemo(
    () =>
      values.salesTaxFiles.length === 0 ? false : values.salesTaxFiles.some((salesTaxFile) => salesTaxFile.uid == null),
    [values.salesTaxFiles]
  );

  const { onEditing } = useContext(CustomersContext);

  const handleFilesSelect = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files) {
        return;
      }

      const base64Files: IFilePayload[] = (
        await Promise.all(
          [...event.target.files].map(async (file) => {
            if (file) {
              const base64Image = await blobToBase64(file, true);

              if (base64Image) {
                return {
                  fileName: file.name,
                  contentType: file.type,
                  content: removeBase64Prefix(base64Image),
                };
              }
            }

            return null;
          })
        )
      ).filter(Boolean) as IFilePayload[];

      await setFieldValue('salesTaxFiles', [...base64Files, ...values.salesTaxFiles], true);
      await setFieldTouched('salesTaxFiles', true);
    },
    [setFieldValue, values.salesTaxFiles, setFieldTouched]
  );

  const handleFileRemove = useCallback(
    async (fileIndex: number) => {
      const newSalesTaxFiles = values.salesTaxFiles.filter((_src, index) => index !== fileIndex);

      await setFieldValue('salesTaxFiles', newSalesTaxFiles, true);
      await setFieldTouched('salesTaxFiles', true);
    },
    [values.salesTaxFiles, setFieldValue, setFieldTouched]
  );

  const handleImageUploadRequest = useCallback(() => fileRef.current?.click(), []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: false
  useEffect(() => {
    onEditing(isSalesTaxFilesDirty);
  }, [isSalesTaxFilesDirty]);

  return (
    <div className="customer-lead-step-sales-tax-info__sales-tax-files">
      <input
        ref={fileRef}
        type="file"
        accept="
          text/csv,
          application/msword,
          application/vnd.openxmlformats-officedocument.wordprocessingml.document,
          application/vnd.ms-excel,
          application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
          application/pdf,
          image/bmp,
          image/gif,
          image/jpeg,
          image/jpeg,
          image/png,
          image/tiff,
          image/vnd.wap.wbmp,
          image/webp,
          image/svg+xml
        "
        hidden={true}
        multiple={true}
        onChange={handleFilesSelect}
      />
      <div>Please upload sales tax files</div>
      <div className="customer-lead-step-sales-tax-info__controls">
        <div
          className="customer-lead-step-sales-tax-info__add-sales-tax-file__button"
          onClick={handleImageUploadRequest}
        >
          <AddPhotoIcon style={{ fill: '#0082F9', width: 48, height: 48 }} />
          <span className="customer-lead-step-sales-tax-info__add-photo__text">Add Sales Tax Files</span>
        </div>

        <div className="flex-3 flex column nowrap gap-small">
          {values.salesTaxFiles.length === 0 ? (
            <>
              <div className="flex row nowrap align-items-center gap-medium">
                <div className="customer-lead-step-sales-tax-info__info-icon">i</div>
                <div className="customer-lead-step-sales-tax-info__info-text">Click on add sales tax files</div>
              </div>

              <div className="flex row nowrap align-items-center gap-medium">
                <div className="customer-lead-step-sales-tax-info__info-icon">i</div>
                <div className="customer-lead-step-sales-tax-info__info-text">
                  You can manage your uploaded files before finishing request
                </div>
              </div>
              <div className="flex row nowrap align-items-center gap-medium">
                <div className="customer-lead-step-sales-tax-info__info-icon">i</div>
                <div className="customer-lead-step-sales-tax-info__info-text">Maximum limit can be up to 100Mb</div>
              </div>
              {errors.salesTaxFiles && (
                <div className="flex row nowrap align-items-center gap-medium">
                  <div className="customer-lead-step-sales-tax-info__info-icon customer-lead-step-sales-tax-info__error-icon">
                    i
                  </div>
                  <div className="customer-lead-step-sales-tax-info__info-text">
                    <ValidationError
                      name="salesTaxFiles"
                      errorType="danger"
                      className="customer-lead-step-sales-tax-info__error-message"
                    />
                  </div>
                </div>
              )}
            </>
          ) : (
            values.salesTaxFiles.map((salesTaxFile, fileIndex) => (
              <div key={salesTaxFile.fileName} className="flex row nowrap align-items-center gap-medium">
                <SalesTaxFileIcon />
                <div className="customer-lead-step-sales-tax-info__info-text flex-1">{salesTaxFile.fileName}</div>
                {!salesTaxFile.uid && (
                  <Button type="link" onClick={() => handleFileRemove(fileIndex)}>
                    Delete
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
